import {Component, Inject, OnInit} from '@angular/core';
import {MatDialog, MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {OrganizationService} from "../../../../../service-data/organization.service";
import {Organization} from "../../../../../../model/Organization";
import {ProjectFolders} from "../../../../../../model/supporting/ProjectFolders";
import {Util} from "../../../../../util/Util";
import {ProjectService} from "../../../../../service-data/project.service";
import {debounceTime} from "rxjs/operators";

@Component({
  selector: 'app-delete-project-folder',
  templateUrl: './delete-project-folder.component.html',
  styleUrls: ['./delete-project-folder.component.css']
})
export class DeleteProjectFolderComponent implements OnInit {
  public breakpoint: number; // Breakpoint observer code
  wasFormChanged = false;
  organization: Organization;
  selectedFolder: string;
  projectFolders: ProjectFolders;
  empty = true;

  constructor(private formBuilder: FormBuilder, public dialog: MatDialog, public organizationService: OrganizationService, private projectService: ProjectService,  @Inject(MAT_DIALOG_DATA) data, private dialogRef: MatDialogRef<DeleteProjectFolderComponent>) {
    // Get data passed in
    if ( data.organization ) {
      this.organization = data.organization;
      this.projectFolders = this.organization.getProjectFolders();
      this.selectedFolder = data.selectedFolder;
    }
  }

  public ngOnInit(): void {
    this.breakpoint = window.innerWidth <= 600 ? 1 : 2; // Breakpoint observer code
    // Evaluate up front rather than on click. The template only renders the
    // Delete button when `empty` is true, so leaving this at its optimistic
    // default meant the user was offered a button that then refused to do
    // anything and replaced itself with an error.
    this.empty = this.isFolderTreeEmpty();
  }

  /**
   * Does this folder, or ANY descendant of it, still hold a Project?
   *
   * Two things this deliberately does not do:
   *
   * 1. It does not use `project.folder.startsWith(selectedFolder)`. That is a
   *    character-prefix test, so deleting `/Test` was blocked by a Project
   *    living in `/Testing`, and a Project in `/TestArchive` made `/Test` look
   *    occupied. `Util.isFolderAtOrUnder` compares whole path segments.
   * 2. It does not treat "no matching Project in the local cache" as proof the
   *    folder is empty when the Project list has not finished loading — an
   *    empty cache trivially satisfies any emptiness test. If the projects
   *    aren't loaded yet we report NOT empty, which fails closed: the worst
   *    outcome is the operator retries in a moment, instead of silently
   *    orphaning Projects into a folder that no longer exists in the tree.
   */
  private isFolderTreeEmpty(): boolean {
    const projects = this.projectService ? this.projectService.objects : undefined;
    if ( !projects || projects.length === 0 ) {
      // Nothing loaded (yet). Cannot prove emptiness -- fail closed.
      return false;
    }
    for ( const project of projects ) {
      if ( !project ) { continue; }
      if ( Util.isFolderAtOrUnder(project.folder, this.selectedFolder) ) {
        return false;
      }
    }
    return true;
  }

  public onChange(): void {
    // Re-check at the moment of action: the list may have grown since ngOnInit
    // (background paging, or a notification from another session).
    this.empty = this.isFolderTreeEmpty();
    if ( !this.empty ) {
      return;
    }
    if ( this.projectFolders ) {
      try {
        this.projectFolders.deleteFolder(this.selectedFolder);
        this.organization.setProjectFolders(this.projectFolders);
        this.organizationService.updateItem(this.organization).subscribe( x => {
          console.log(`Delete Folder completed ${this.selectedFolder}`);
        });
      } catch ( e ) {
        console.log(`Delete Folder not completed ${this.selectedFolder}`);
      }
    }

    const returnObject = {};
    this.dialogRef.close(returnObject);
  }

  // tslint:disable-next-line:no-any
  public onResize(event: any): void {
    this.breakpoint = event.target.innerWidth <= 600 ? 1 : 2;
  }

  formChanged() {
    this.wasFormChanged = true;
  }
}
